import { useState, useRef, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { saveSelection, restoreSelection } from '../../services/selectionUtils';
import { 
  User, 
  CaretPosition, 
  UseRichCommentInputReturn, 
  RichCommentInputProps,
  Selection
} from './types';
import { renderUserSuggestions as createUserSuggestions } from './UserSuggestions';

// window.handleUserProfileClickを拡張
declare global {
  interface Window {
    handleUserProfileClick?: (username: string) => void;
    handleSeekToTime?: (seconds: number) => void;
  }
}

/**
 * RichCommentInputのロジック部分を実装するカスタムフック
 */
export const useRichCommentInput = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  onSeekToTime
}: Pick<RichCommentInputProps, 'value' | 'onChange' | 'onSubmit' | 'disabled' | 'onSeekToTime'>): UseRichCommentInputReturn => {
  const divRef = useRef<HTMLDivElement>(null);
  const caretToEndRef = useRef<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [showUserSuggestions, setShowUserSuggestions] = useState<boolean>(false);
  const [userQuery, setUserQuery] = useState<string>('');
  const [userSuggestionPosition, setUserSuggestionPosition] = useState<CaretPosition>({ top: 0, left: 0 });
  // メンションされたユーザー名を記録する配列
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  // 選択中のユーザーインデックス
  const [selectedUserIndex, setSelectedUserIndex] = useState<number>(0);
  // 日本語入力中かどうか（IME入力追跡用）
  const [isComposing, setIsComposing] = useState<boolean>(false);
  // IME入力開始前の選択範囲を保存
  const compositionStartSelectionRef = useRef<Selection | null>(null);

  // #xx:yy を <span class="seek-token"> に置き換える処理
  // @username も <span class="mention-token"> に置き換える
  // URLも <a class="url-link"> に置き換える
  const getTransformedHTML = (text: string): string => {
    // null や undefined の場合は空文字列として扱う
    if (!text) {
      return '';
    }
    
    const escapeHTML = (str: string): string =>
      str
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const escaped = escapeHTML(text);
    
    // 最初に時間タグを変換
    let transformed = escaped.replace(
      /(#[0-9]{1,2}:[0-9]{2})(?=\s|$)/g,
      '<span class="seek-token" onclick="(function(event) { event.stopPropagation(); const match = \'$1\'.match(/#(\\d{1,2}):(\\d{2})/); if (match) { const minutes = parseInt(match[1], 10); const seconds = parseInt(match[2], 10); const totalSeconds = minutes * 60 + seconds; if (window.handleSeekToTime) { window.handleSeekToTime(totalSeconds); window.scrollTo({ top: 0, behavior: \'smooth\' }); } } })(event)" title="クリックすると指定の時間へ移動し、画面の最上部にスクロールします">$1</span>'
    );
    
    // URLを変換（http://, https://, www.で始まるもの）
    transformed = transformed.replace(
      /(https?:\/\/[^\s]+)|(www\.[^\s]+\.[^\s]+)/g,
      (match) => {
        let url = match;
        // www.から始まる場合はhttpsを付加
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        }
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="url-link">${match}</a>`;
      }
    );
    
    // メンションを変換
    // 常に直接入力とメンションリスト両方を考慮する（二重置換を防ぐため）
    
    // 1. 直接入力の@パターンまたはメンションリストの両方を考慮するメンション変換処理
    const processedNames = new Set<string>();
    
    // メンションリスト内のユーザーを処理
    if (mentionedUsers.length > 0) {
      mentionedUsers.forEach(username => {
        // ユーザー名をエスケープして正規表現で安全に使えるようにする
        const escapedUsername = username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`(\\s|^)@(${escapedUsername})(?=\\s|$)`, 'g');
        
        transformed = transformed.replace(
          pattern, 
          `$1<span class="seek-token mention-token" title="${escapedUsername}さんへのメンション" 
          onclick="
            (function(event) {
              event.stopPropagation();
              const target = event.currentTarget;
              const originalBg = target.style.backgroundColor;
              const originalTransform = target.style.transform;
              
              target.style.backgroundColor = '#b3d7ff';
              target.style.transform = 'translateY(-2px) scale(1.05)';
              
              setTimeout(() => {
                target.style.backgroundColor = originalBg;
                target.style.transform = originalTransform;
              }, 300);
              
              if(window.handleUserProfileClick) window.handleUserProfileClick('$2');
            })(event)
          ">@$2</span>`
        );
        
        processedNames.add(username);
      });
    }
    
    // 2. メンションリストで処理されなかった直接入力の@パターンを処理
    transformed = transformed.replace(
      /(\s|^)@([^\s]+)(?=\s|$)/g,
      (match, p1, p2) => {
        // すでに処理したユーザー名はスキップ
        if (processedNames.has(p2)) {
          return match;
        }
        
        return `${p1}<span class="seek-token mention-token" title="${p2}さんへのメンション" 
        onclick="
          (function(event) {
            event.stopPropagation();
            const target = event.currentTarget;
            const originalBg = target.style.backgroundColor;
            const originalTransform = target.style.transform;
            
            target.style.backgroundColor = '#b3d7ff';
            target.style.transform = 'translateY(-2px) scale(1.05)';
            
            setTimeout(() => {
              target.style.backgroundColor = originalBg;
              target.style.transform = originalTransform;
            }, 300);
            
            if(window.handleUserProfileClick) window.handleUserProfileClick('${p2}');
          })(event)
        ">@${p2}</span>`;
      }
    );
    
    return transformed;
  };

  // カーソルを先頭にセットする関数
  const setCaretAtBeginning = (): void => {
    if (divRef.current) {
      const range = document.createRange();
      range.selectNodeContents(divRef.current);
      range.collapse(true);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>): void => {
    if (!disabled) {
      // IME入力中は処理をスキップ
      if (isComposing) return;
      
      onChange(e.currentTarget.innerText);
      
      // @記号の後の文字列を取得し、ユーザーサジェストを表示するかどうかを判断
      const text = e.currentTarget.innerText;
      const caretPosition = getCaretPosition();
      const textBeforeCaret = text.substring(0, caretPosition);
      
      // 最後の@から現在のキャレット位置までの文字列を抽出
      // スペースや行頭の直後の@のみを考慮する
      const lastAtPos = findLastMentionStart(textBeforeCaret);
      
      if (lastAtPos !== -1) {
        const query = textBeforeCaret.substring(lastAtPos + 1);
        // @の後に空白がなければサジェストを表示
        if (!query.includes(' ')) {
          setUserQuery(query);
          
          // カーソル位置に基づいてサジェスト表示位置を設定
          const position = getCaretCoordinates();
          setUserSuggestionPosition(position);
          setShowUserSuggestions(true);
          
          // ユーザー検索
          fetchUsers(query);
        } else {
          setShowUserSuggestions(false);
          
          // 直接入力されたメンションを検出して追加（最後の@以降に空白がある場合）
          const mentionMatch = /@([^\s]+)/.exec(query);
          if (mentionMatch && mentionMatch[1]) {
            const username = mentionMatch[1];
            
            // すでに存在しない場合のみ追加
            setMentionedUsers(prevUsers => {
              if (!prevUsers.includes(username)) {
                return [...prevUsers, username];
              }
              return prevUsers;
            });
          }
        }
      } else {
        setShowUserSuggestions(false);
      }
      
      // 全テキストからメンションを検出
      const mentionRegex = /(\s|^)@([^\s]+)(?=\s|$)/g;
      let match;
      const detected: string[] = [];
      
      // テキスト全体から @username パターンを検出
      while ((match = mentionRegex.exec(text)) !== null) {
        const username = match[2];
        if (username && !detected.includes(username)) {
          detected.push(username);
        }
      }
      
      // 検出されたメンションを既存のリストと比較して更新
      if (detected.length > 0) {
        setMentionedUsers(prevUsers => {
          const newUsers = [...prevUsers];
          detected.forEach(username => {
            if (!newUsers.includes(username)) {
              newUsers.push(username);
            }
          });
          return newUsers;
        });
      }
    }
  };
  
  // 日本語入力開始時のイベントハンドラ
  const handleCompositionStart = (e: React.CompositionEvent<HTMLDivElement>): void => {
    setIsComposing(true);
    // 入力開始時の選択範囲を保存
    if (divRef.current) {
      compositionStartSelectionRef.current = saveSelection(divRef.current) as Selection | null;
    }
  };
  
  // 日本語入力確定時のイベントハンドラ
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLDivElement>): void => {
    setIsComposing(false);
    // 入力が確定したらテキスト変更を通知
    const innerText = e.currentTarget.innerText || '';
    onChange(innerText);
    
    // 少し遅延させて選択範囲を復元（IME確定後の挙動を安定させるため）
    setTimeout(() => {
      // 入力確定後の最適なカーソル位置に調整
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const caretPos = getCaretPosition();
        
        // 通常のinputイベントでカーソル位置の処理をトリガー
        handleInput(e as unknown as React.FormEvent<HTMLDivElement>);
      }
    }, 10);
  };
  
  // メンション開始位置の検出（単語の先頭の@のみを検出）
  const findLastMentionStart = (text: string): number => {
    // 最後の@の位置
    const lastAtPos = text.lastIndexOf('@');
    if (lastAtPos === -1) return -1;
    
    // @の直前が空白または行頭かどうかをチェック
    if (lastAtPos === 0 || /\s/.test(text.charAt(lastAtPos - 1))) {
      return lastAtPos;
    }
    
    return -1;
  };

  // カーソル位置を取得
  const getCaretPosition = (): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;
    
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    if (divRef.current) {
      preCaretRange.selectNodeContents(divRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  };
  
  // カーソル座標を取得
  const getCaretCoordinates = (): CaretPosition => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return { top: 0, left: 0 };
    
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const divRect = divRef.current?.getBoundingClientRect();
    
    if (divRect) {
      return {
        top: rect.bottom - divRect.top,
        left: rect.left - divRect.left
      };
    }
    
    return { top: 0, left: 0 };
  };
  
  // ユーザーを検索
  const fetchUsers = async (query: string): Promise<void> => {
    try {
      const response = await axios.get(`/api/users/search?q=${query}`);
      setUsers(response.data.data || []);
      // ユーザーリストが更新されたら選択インデックスをリセット
      setSelectedUserIndex(0);
    } catch (error) {
      console.error('ユーザー検索エラー:', error);
      setUsers([]);
    }
  };
  
  // ユーザー名をクリックしたときの処理
  const handleUserClick = (userName: string): void => {
    if (!divRef.current) return;
    
    const text = divRef.current.innerText;
    const caretPosition = getCaretPosition();
    const textBeforeCaret = text.substring(0, caretPosition);
    
    // 最後の@から現在のキャレット位置までを置換
    const lastAtPos = findLastMentionStart(textBeforeCaret);
    if (lastAtPos !== -1) {
      // スペースを追加して確実にメンションが区切られるようにする
      const newText = text.substring(0, lastAtPos) + '@' + userName + ' ' + text.substring(caretPosition);
      onChange(newText);
      
      // メンションされたユーザー名を記録する
      setMentionedUsers(prevUsers => {
        if (!prevUsers.includes(userName)) {
          return [...prevUsers, userName];
        }
        return prevUsers;
      });
      
      setShowUserSuggestions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    // IME入力中はEnterキーの特別処理をスキップ
    if (isComposing) {
      return;
    }
    
    // ユーザーサジェスト表示中の場合
    if (showUserSuggestions && users.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // 次のユーザーを選択（最後まで行ったら先頭に戻る）
        setSelectedUserIndex(prevIndex => (prevIndex + 1) % users.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        // 前のユーザーを選択（先頭から前に行ったら最後に戻る）
        setSelectedUserIndex(prevIndex => (prevIndex - 1 + users.length) % users.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        // 現在選択中のユーザーを確定
        if (users[selectedUserIndex]) {
          handleUserClick(users[selectedUserIndex].name);
        }
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowUserSuggestions(false);
        return;
      }
    }
    
    // Ctrl/Command + Enter で送信
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit(e);
    }
    // Enter のみで改行（<div>で改行）を挿入
    else if (e.key === 'Enter') {
      // サジェスト表示中でなければ改行を挿入
      if (!showUserSuggestions) {
        e.preventDefault();
        caretToEndRef.current = true;
        document.execCommand('insertHTML', false, '\n');
      }
    }
  };

  // フォーカス時、内容が空なら placeholder 相当のテキストを消してカーソルを先頭へ
  const handleFocus = (): void => {
    if (disabled) {
      // 無効時はフォーカスを外す
      divRef.current?.blur();
      return;
    }
    
    if (divRef.current && divRef.current.innerText && divRef.current.innerText.trim() === '') {
      divRef.current.innerHTML = '';
      setCaretAtBeginning();
    } else if (divRef.current && !divRef.current.innerText) {
      // innerTextがundefinedの場合も空にする
      divRef.current.innerHTML = '';
      setCaretAtBeginning();
    }
  };

  // ユーザーサジェストをクリックした際に、コメントが消えないようにする
  const handleSuggestionMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
  };

  // サーバーからのユーザーリストを表示するコンポーネント
  const renderUserSuggestions = (): ReactNode => {
    return showUserSuggestions && users.length > 0
      ? createUserSuggestions({
          users,
          selectedUserIndex,
          position: userSuggestionPosition,
          onMouseDown: handleSuggestionMouseDown,
          onUserClick: handleUserClick,
          onMouseEnter: setSelectedUserIndex
        })
      : null;
  };

  // value が変わったタイミングで innerHTML を更新し、カーソルを保存・復元
  useEffect(() => {
    if (divRef.current) {
      // 日本語入力中はHTMLの更新を行わない
      if (isComposing) return;
      
      const savedSel = saveSelection(divRef.current) as Selection | null;
      const newHTML = getTransformedHTML(value);
      if (divRef.current.innerHTML !== newHTML) {
        divRef.current.innerHTML = newHTML;
        if (caretToEndRef.current) {
          const range = document.createRange();
          range.selectNodeContents(divRef.current);
          range.collapse(false);
          const sel = window.getSelection();
          if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
          }
          caretToEndRef.current = false;
        } else if (savedSel) {
          restoreSelection(divRef.current, savedSel);
        }
      }
    }
  }, [value, mentionedUsers, isComposing]);

  // 日本語入力中のコンテンツ保護用
  useEffect(() => {
    // エッジケース: コンポーネント外部から参照を保護
    const handleBeforeInput = (e: InputEvent): void => {
      if (isComposing && divRef.current && document.activeElement === divRef.current) {
        // 特定の問題のある操作をキャンセル
        if (e.inputType === 'deleteContentBackward' && e.data === null) {
          e.preventDefault();
        }
      }
    };

    // Safariなどで日本語入力中に問題が生じることがあるので追加
    const handleCompositionUpdate = (e: CompositionEvent): void => {
      if (divRef.current && isComposing) {
        // 何もしない - 単に処理中のフラグを維持
      }
    };

    // イベントリスナーの追加
    if (divRef.current) {
      divRef.current.addEventListener('beforeinput', handleBeforeInput as EventListener);
      divRef.current.addEventListener('compositionupdate', handleCompositionUpdate as EventListener);
    }

    // クリーンアップ
    return () => {
      if (divRef.current) {
        divRef.current.removeEventListener('beforeinput', handleBeforeInput as EventListener);
        divRef.current.removeEventListener('compositionupdate', handleCompositionUpdate as EventListener);
      }
    };
  }, [isComposing]);

  // コンポーネント初期化時にテキスト内の既存のメンションを検出
  useEffect(() => {
    if (value) {
      // @ユーザー名のパターンに一致する部分をすべて抽出
      const regex = /(\s|^)@([a-zA-Z0-9_.-]+@[a-zA-Z0-9_.-]+\.[a-zA-Z0-9_.-]+)(?=\s|$)|(\s|^)@([a-zA-Z0-9_.-]+)(?=\s|$)|(\s|^)@([^\s]+)(?=\s|$)/g;
      let match;
      const detected: string[] = [];
      
      while ((match = regex.exec(value)) !== null) {
        // メールアドレスの場合はグループ2、一般的なユーザー名はグループ4、その他の文字列はグループ6にマッチする
        const username = match[2] || match[4] || match[6];
        if (username && !detected.includes(username)) {
          detected.push(username);
        }
      }
      
      if (detected.length > 0) {
        setMentionedUsers(detected);
      }

      // 値が存在する場合は内容を更新
      if (divRef.current) {
        divRef.current.innerHTML = getTransformedHTML(value);
      }
    } else {
      // 値が空の場合は空にして、プレースホルダーが表示されるようにする
      if (divRef.current) {
        divRef.current.innerHTML = '';
      }
    }
  }, []);

  // onSeekToTimeのグローバル関数を設定
  useEffect(() => {
    if (onSeekToTime) {
      window.handleSeekToTime = onSeekToTime;
    }
    
    // クリーンアップ
    return () => {
      if (window.handleSeekToTime) {
        delete window.handleSeekToTime;
      }
    };
  }, [onSeekToTime]);

  return {
    divRef,
    users,
    showUserSuggestions,
    userSuggestionPosition,
    selectedUserIndex,
    mentionedUsers,
    isComposing,
    handleInput,
    handleKeyDown,
    handleFocus,
    handleCompositionStart,
    handleCompositionEnd,
    handleUserClick,
    handleSuggestionMouseDown,
    renderUserSuggestions,
    getTransformedHTML
  };
};

export default useRichCommentInput; 